<template>
  <div class="global-language-selector-container">
    <div class="language-selector-container">
      <div :class="[{ 'global-language-selector-selected': lang === selectedLang }]" :ref="'globalLanguage' + lang"
        v-for="lang in languages" :key="lang" @click="selectLanguage(lang)">
        <span :class="['global-language-selector', 'globalLanguage' + lang]">
          {{ lang === undefined || lang.toUpperCase() === 'UND' || lang === '' || lang.toUpperCase() ===
            'NULL' ? "&nbsp;" :
            lang.toUpperCase() }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import ConceptsStore from "../concepts-store";
import StoreConfiguration from '../store-configuration';
import BlankNodeTypeEnum from '../enums/BlankNodes';
import Utils from "../utils";

export default {
  name: "AttributeLanguageSelector",
  components: {},

  data() {
    return {
      store: ConceptsStore.state,
      storeConfiguration: StoreConfiguration.state,
      languagesIncludingHidden: [],
      languages: [],
      selectedLang: ''
    }
  },

  /**
   * Surveille les changements de la route pour rafraîchir le composant
   * et pour sélectionner la langue si elle est définie dans les paramètres de la route.
   */
  watch: {
    $route(to, from) {
      if (this.$route.query.lang !== undefined && to.query.lang !== from.query.lang) {
        this.selectLanguage(this.$route.query.lang.toUpperCase());
        this.$nextTick(() => {
          this.refreshComponent();
        });
      }
    },

    'store.configurationsFiltresAxiomesChange'() {
      this.$nextTick(() => {
        this.refreshComponent();
      });
    }
  },

  /**
   * Montée du composant :
   * - Désabonne l'événement 'refresh-attribute-language-selector' pour éviter les doublons.
   * - S'abonne à l'événement 'refresh-attribute-language-selector' pour rafraîchir le composant.
   * - Appelle la méthode `refreshComponent` pour initialiser l'affichage.
   */
  mounted() {
    this.refreshComponent();
    if (this.languages && this.languages.length > 0 && this.$route.query.lang !== undefined) {
      this.selectLanguage(this.$route.query.lang.toUpperCase());
    }
  },

  props: {
    blankNodes: {
      type: Array,
      default: () => [],
    },
    simpleValues: {
      type: Array,
      default: () => [],
    },
  },

  methods: {

    /**
     * La méthode `refreshComponent` est utilisée pour rafraîchir l'affichage du composant.
     * Elle récupère les configurations de filtres d'axiomes depuis le localStorage,
     */
    refreshComponent() {
      const configurationsFiltresAxiomes = JSON.parse(localStorage.getItem('configurationsFiltresAxiomes'));
      let blankNodesToShow = JSON.parse(JSON.stringify(this.blankNodes));
      let logicalAxiomesChecked = false;
      let anonymousAxiomesChecked = false;
      this.languagesIncludingHidden = Utils.processLanguages(this.blankNodes, this.storeConfiguration, this.simpleValues).languages;


      configurationsFiltresAxiomes.forEach(config => {
        if (config.terminologyId === this.store.terminologyId) {
          logicalAxiomesChecked = config.configuration.logicalAxiomesChecked;
          anonymousAxiomesChecked = config.configuration.anonymousAxiomesChecked;
        }
      });

      //filtrer les axiomes logiques niveau 0
      if (!logicalAxiomesChecked) {
        blankNodesToShow = blankNodesToShow.filter(blankNode => {
          return blankNode.blankNodeType !== BlankNodeTypeEnum.LOGICAL_AXIOM;
        });
      }
      //filtrer les axiomes anonymes niveau 0
      if (!anonymousAxiomesChecked) {
        // filter 
        blankNodesToShow = blankNodesToShow.filter(blankNode => {
          return blankNode.blankNodeType !== BlankNodeTypeEnum.ANONYMOUS_ANNOTATION;
        });
      }


      this.languages = Utils.processLanguages(blankNodesToShow, this.storeConfiguration, this.simpleValues).languages;
      if (this.languages === undefined || this.languages.length === 0) {
        this.languages = ['FR'];
      }

      if (this.$route.query.lang !== undefined) {
        this.selectLanguage(this.$route.query.lang.toUpperCase());
      }

      this.$forceUpdate();
    },



    /**
     * La méthode `selectLanguage` permet de sélectionner une langue spécifique dans le sélecteur de langues.
     * Elle met à jour l'affichage en fonction de la langue sélectionnée et applique les classes CSS appropriées
     * @param lang 
     */
    selectLanguage(lang) {
      // Remove 'global-language-selector-selected' class from all buttons
      if (this.languages === undefined) {
        return;
      }
      if (lang === undefined || lang === null || lang === '') {
        lang = this.languages[0];
      }

      lang = lang.toUpperCase();
      if (this.languages.includes(lang) === false) {
        lang = 'FR';
      }
      if (this.languages.includes(lang) === false) {
        lang = 'UND';
      }
      if (this.languages.includes(lang) === false) {
        lang = this.languages[0];
      }



      this.selectedLang = lang;
      this.languages.forEach(language => {
        let button = this.$refs[`globalLanguage${language}`];
        if (button && Array.isArray(button)) {
          button = button[0];
        }
        if (button && button.classList) {
          button.classList.remove('global-language-selector-selected');
        }
      });

      // Add 'global-language-selector-selected' class to the selected button
      let selectedButton = this.$refs[`globalLanguage${lang}`];
      if (selectedButton && Array.isArray(selectedButton)) {
        selectedButton = selectedButton[0];
      }
      if (selectedButton) {
        selectedButton.classList.add('global-language-selector-selected');
      }
      this.showOnlyLanguage(lang);

    },


    /**
     * Met à jour l'affichage pour ne montrer que les éléments de la langue spécifiée.
     * Cette méthode parcourt tous les éléments de l'arbre et masque ceux qui ne correspondent pas à la langue spécifiée.
     * @param lang 
     */
    showOnlyLanguage(lang) {
      let languagesConf = Utils.convertObjectValuesToArray(this.storeConfiguration.current?.concepts_details?.affichage?.ordre_langues);
      let languesParPriorite = [];
      languesParPriorite.push(lang);
      languesParPriorite = languesParPriorite.concat(languagesConf);
      languesParPriorite = languesParPriorite.concat(this.languagesIncludingHidden);

      this.$parent.$el.querySelectorAll(".filter-lang-dedicated").forEach(obj => {
        obj.classList.add('hidden');
      });

      let languesParPrioriteUnique = Array.from(new Set(languesParPriorite));
      this.$parent.$el.querySelectorAll('.tree-node').forEach(element => {

        for (let index in languesParPrioriteUnique) {
          let l = languesParPrioriteUnique[index]
          if (element.querySelectorAll(`.filter-lang-dedicated-${l}`).length > 0) {
            let elements = element.querySelectorAll(`.filter-lang-dedicated-${l}`);
            elements.forEach(obj => {
              obj.classList.remove('hidden');
            });
            break;
          }
        }
      });
    },
  },

};
</script>



<style lang="scss" scoped>
.language-selector-container {
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: start;
  cursor: pointer;
}

.global-language-selector {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 23px;
  min-width: 23px;
  height: 23px;
  min-height: 23px;
  margin: 0px 15px 5px 15px !important;
  padding: 0;
  border-radius: 50px;
  text-align: center;
  border: 1px solid var(--v-tertiary-base);
  font-size: 10px;
  letter-spacing: normal;
  font-weight: 500;
  color: var(--v-tertiary-base);
}

language-selector {
  margin-right: 10px;
}

.global-language-selector-container {
  align-content: flex-end;
  padding-bottom: 1%;
}

.global-language-selector-selected {
  border-bottom: solid 2px var(--v-primary-base);
}

.global-language-selector-selected>* {
  border-color: var(--v-primary-base);
  color: var(--v-primary-base);
}
</style>